/**
 * Install and platform detection.
 *
 * This exists because of one fact: on iOS, Web Push does not work at all until
 * the app has been added to the home screen. A participant who skips that step
 * silently receives nothing for five days and we only find out at analysis. So
 * install is a guided screen, not a hint, and it comes before we ask for
 * notification permission.
 */

/* ------------------------------------------------------------------ *
 * beforeinstallprompt fires early, once, and only on Chromium. Capture it at
 * module load or it is gone by the time the participant reaches the screen.
 * ------------------------------------------------------------------ */

let deferredPrompt = null
const listeners = new Set()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault() // we choose the moment, not the browser
    deferredPrompt = event
    listeners.forEach((fn) => fn())
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    listeners.forEach((fn) => fn())
  })
}

export function onInstallStateChange(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function canPromptInstall() {
  return deferredPrompt !== null
}

/** Chromium's real install dialog. Returns true if the participant accepted. */
export async function promptInstall() {
  if (!deferredPrompt) return false
  const event = deferredPrompt
  deferredPrompt = null
  listeners.forEach((fn) => fn())
  event.prompt()
  const choice = await event.userChoice
  return choice?.outcome === 'accepted'
}

/* ------------------------------------------------------------------ *
 * Platform. Used only to pick which install instructions to show.
 * ------------------------------------------------------------------ */

export const PLATFORM = {
  IOS: 'ios',
  ANDROID: 'android',
  DESKTOP: 'desktop',
}

export function detectPlatform(ua = navigator.userAgent, nav = navigator) {
  // iPadOS 13+ reports itself as a Mac, so touch points are the tell.
  const isIpad = /Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1
  if (/iPhone|iPad|iPod/.test(ua) || isIpad) return PLATFORM.IOS
  if (/Android/.test(ua)) return PLATFORM.ANDROID
  return PLATFORM.DESKTOP
}

/** True when running from the home screen rather than in a browser tab. */
export function isStandalone() {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    window.navigator.standalone === true // iOS Safari's own flag
  )
}

/** On iOS, only Safari can add to the home screen. */
export function isIosSafari(ua = navigator.userAgent) {
  if (detectPlatform(ua) !== PLATFORM.IOS) return false
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
}

export function supportsPush() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function notificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported'
  return Notification.permission // 'default' | 'granted' | 'denied'
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null)
  return navigator.serviceWorker.register('/sw.js', { scope: '/' })
}
